import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { FEES, FIRM, FIRM_ADDRESS, PENINSULA_ADDRESS, faqSchema } from "@/lib/firm";

const DOES = [
  {
    t: "Reads the contract before you sign",
    d: "Special conditions, the settlement and finance dates, what is included, disclosure attachments, body corporate material and anything the agent has drafted in by hand. This is the only point where terms can still be changed.",
  },
  {
    t: "Orders and reads the searches",
    d: "Title, registered plan, easements and covenants, council rates and water, land tax, transport and contaminated land registers, and body corporate records for units. Searches exist to find the things nobody mentions at the inspection.",
  },
  {
    t: "Runs the conditional period",
    d: "Cooling-off, finance approval and building and pest, each with a date and a consequence. Extensions, objections and terminations all have to be given in the right form, to the right person, before the deadline.",
  },
  {
    t: "Deals with duty",
    d: "Assessing transfer duty, applying the home or first home concession you qualify for, and paying it so the transfer can be lodged.",
  },
  {
    t: "Prepares the adjustments",
    d: "Rates, water and body corporate levies are apportioned to the day of settlement, and the balance owing is calculated to the cent with the other side.",
  },
  {
    t: "Settles and reports",
    d: "Electronic settlement through PEXA, then notices to council, the water authority and the body corporate, and a final statement showing exactly where the money went.",
  },
];

const FAQS = [
  {
    q: "What does a conveyancer do in Queensland?",
    a: "A conveyancer handles the legal transfer of property ownership: reviewing the contract before signing, ordering and interpreting searches, managing the cooling-off, finance and building-and-pest conditions, assessing and paying transfer duty, calculating rates and water adjustments, and completing electronic settlement through PEXA before notifying council and the water authority of the change of ownership.",
  },
  {
    q: "Can you be a licensed conveyancer in Queensland?",
    a: "No. Queensland is different from New South Wales, Victoria and most other states — there is no licensed conveyancer profession here. Conveyancing in Queensland is legal work that must be done by, or under the supervision of, a solicitor holding a current practising certificate. Businesses trading as 'conveyancers' in Queensland are either operating under a law practice or restricted to clerical work.",
  },
  {
    q: "Do I need a conveyancer or a solicitor in Queensland?",
    a: "In Queensland the question does not really arise: the work is done by a solicitor either way. What varies is who you actually deal with — an admitted solicitor who reads your contract, or a processing centre where a paralegal moves the file along and a solicitor signs off at volume. If a title problem, an unapproved structure or a dispute over a special condition appears, that difference matters.",
  },
  {
    q: "How much does conveyancing cost in Queensland?",
    a: `Our professional fee is fixed and agreed before we start: ${FEES.sale} incl. GST on a residential sale and ${FEES.purchase} incl. GST on a purchase, plus searches, PEXA and Titles Registry fees at cost. Reviewing a contract before you sign is free. Transfer duty is a separate government charge.`,
  },
  {
    q: "When should I involve a conveyancer?",
    a: "Before you sign, not after. Once the contract is signed your options narrow to whatever the conditions allow. A pre-signing review costs you nothing with us and is the one point at which a special condition can still be added, a date made workable or a disclosure gap raised.",
  },
  {
    q: "How long does conveyancing take in Queensland?",
    a: "A standard REIQ contract runs 30 to 45 days: five business days cooling-off, finance and building and pest usually at 14 to 21 days, then settlement. Off-the-plan purchases run to plan registration instead and can take months, which makes the sunset date the important one.",
  },
];

export const Route = createFileRoute("/what-does-a-conveyancer-do-qld")({
  head: () => ({
    meta: [
      { title: fitTitle(`What Does a Conveyancer Do in Queensland? | ${FIRM.name}`) },
      {
        name: "description",
        content:
          fitDescription("What a conveyancer actually does in Queensland, why the work must be done by a solicitor here, what it costs, and how long a standard settlement takes."),
      },
      { property: "og:title", content: fitTitle(`What Does a Conveyancer Do in Queensland? | ${FIRM.name}`) },
      {
        property: "og:description",
        content:
          fitDescription("Contract review, searches, conditions, duty, adjustments and settlement — the whole job, explained."),
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: "https://coastsidelaw.com.au/what-does-a-conveyancer-do-qld" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/what-does-a-conveyancer-do-qld" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: ConveyancerPage,
});

function ConveyancerPage() {
  return (
    <>
      <PageHero
        eyebrow="Conveyancing · Queensland"
        title="What does a conveyancer do in Queensland?"
        lead="Short answer: everything between the handshake and the keys — and in Queensland, unlike most other states, that work has to be done by a solicitor. Here is the job in full, what it costs, and where it usually goes wrong."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="rounded-sm border border-border bg-secondary/50 p-6">
          <p className="text-sm leading-relaxed text-muted-foreground">
            <strong className="text-foreground">In Queensland there is no licensed conveyancer.</strong>{" "}
            New South Wales and Victoria license conveyancers as a separate profession. Queensland
            does not. Conveyancing here is legal practice, so it must be carried out by a solicitor
            with a current practising certificate. When you hire a Queensland "conveyancer", you are
            hiring a law practice — the only real question is whether you speak to the solicitor.
          </p>
        </div>

        <h2 className="mt-14 font-serif text-3xl text-foreground">The job, step by step</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {DOES.map((d) => (
            <div key={d.t} className="rounded-sm border border-border bg-card p-5">
              <h3 className="font-serif text-lg text-foreground">{d.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{d.d}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-14 font-serif text-3xl text-foreground">What it costs</h2>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Our fees are fixed and agreed before any work starts: {FEES.sale} incl. GST on a
          residential sale, {FEES.purchase} incl. GST on a purchase, plus searches, PEXA and Titles
          Registry fees at cost. Reviewing your contract before you sign is free, with no obligation.
          Transfer duty is a government charge on top — you can estimate it with our{" "}
          <Link to="/stamp-duty-calculator-qld" className="font-semibold text-primary underline">
            Queensland stamp duty calculator
          </Link>
          .
        </p>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Solicitor-led, not a processing centre</h2>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          The cheapest national operators run high volumes through call centres, and for a clean
          transaction that works out fine. It stops working when the title carries an easement nobody
          mentioned, when the deck was never approved, when the seller has died mid-contract, or when
          a special condition has been written to favour the other side. At Coastside Law you deal
          with the solicitor doing the work, from {FIRM_ADDRESS} on the Sunshine Coast and{" "}
          {PENINSULA_ADDRESS} on the Redcliffe Peninsula.
        </p>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Where we act</h2>
        <ul className="mt-5 space-y-2 text-sm">
          <li>
            <Link to="/golden-beach" className="font-semibold text-primary underline">
              Golden Beach and Caloundra conveyancing
            </Link>
          </li>
          <li>
            <Link
              to="/conveyancing/$suburb"
              params={{ suburb: "redcliffe" }}
              className="font-semibold text-primary underline"
            >
              Redcliffe and Moreton Bay conveyancing
            </Link>
          </li>
          <li>
            <Link to="/pexa-settlement" className="font-semibold text-primary underline">
              How PEXA settlement works
            </Link>
          </li>
          <li>
            <Link to="/conveyancing" className="font-semibold text-primary underline">
              Fixed-fee conveyancing across Queensland
            </Link>
          </li>
        </ul>

        <PeopleStrip slugs={["erin-wilkinson", "michael-klein"]} heading="Who will act for you" />

        <h2 className="mt-14 font-serif text-3xl text-foreground">Common questions</h2>
        <div className="mt-6">
          <FaqList items={FAQS} />
        </div>

        <div className="mt-14">
          <EnquiryForm heading="Get a fixed-fee conveyancing quote" />
        </div>
      </section>

      <CtaBand
        title="Have a contract in front of you?"
        body={`Send it through before you sign — we read it at no charge and tell you what needs to change. Call ${FIRM.phone}.`}
      />
      <div className="h-20" />
    </>
  );
}
